import { Horizontal, Vertical } from '@eventbrite/eds-layout';
import { DisplayAmount } from '@eventbrite/eds-read-more';
import { ChoiceCard } from '@eventbrite/marmalade';
import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { TicketAvailabilityFieldProps } from '../../../containers/ticketSelection/TicketAvailabilityField';
import { shouldShowTicketReseller } from '../../../containers/ticketSelection/utils/ticketUtils';
import { TicketStatus } from '../../../models/tickets';
import { UrgencySignals } from '../../../models/urgencySignals';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import Divider, { DividerDirection } from '../../divider/Divider';
import {
    NOT_AVAILABLE_FOR_SALE_STATUSES,
    TicketCommonProps,
    TicketMappedImage,
} from '../constants';
import { TicketCardStatusProps } from '../TicketCardStatus';
import { TicketContentProps } from '../TicketContent';
import TicketDescription from '../TicketDescription';
import { TicketJoinWaitlistButton } from '../TicketJoinWaitlistButton/TicketJoinWaitlistButton';
import TicketName from '../TicketName';
import type { TicketPriceProps } from '../TicketPrice';
import TicketPriceCountDateAndThumbnail from '../TicketPriceCountDateAndThumbnail/TicketPriceCountDateAndThumbnail';
import TicketQuantityPicker, {
    TicketQuantityPickerProps,
} from '../TicketQuantityPicker';
import { TicketResellerButton } from '../TicketResellerButton';
import type { TicketSalesEndDateProps } from '../TicketSalesEndDate';
import {
    getDonationChangeActionPayload,
    getIsCardDisabledByStatus,
} from '../utils';
import defaultStyles from './TicketDisplayCardContentFullSize.module.scss';
import rebrandStyles from './TicketDisplayCardContentFullSize.rebranding.module.scss';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

export interface TicketDisplayCardContentFullSizeProps
    extends Pick<TicketCommonProps, 'description'>,
        Pick<
            TicketPriceProps,
            'feesAndTaxes' | 'price' | 'priceWithoutDiscounts'
        >,
        Pick<
            TicketSalesEndDateProps,
            'formattedSalesEndDate' | 'hideSaleDates'
        >,
        Pick<TicketCardStatusProps, 'statusKey'>,
        Pick<
            TicketQuantityPickerProps,
            | 'formattedSalesStartDate'
            | 'isDonation'
            | 'name'
            | 'onQuantityChange'
            | 'selectableQuantities'
            | 'selectedQuantity'
            | 'ticketId'
            | 'ticketStatus'
        >,
        Pick<
            TicketContentProps,
            | 'category'
            | 'cost'
            | 'currencyFormat'
            | 'currencySymbol'
            | 'donationAmount'
            | 'hideSelect'
            | 'id'
            | 'includeFee'
            | 'includeTax'
            | 'isCurrencySuffix'
        >,
        Pick<
            TicketAvailabilityFieldProps,
            'onJoinWaitlist' | 'showJoinWaitlist' | 'ticketReseller'
        > {
    image?: TicketMappedImage;
    isAppRebrandActive: boolean;
    isBOGOTicket: boolean;
    isFirstTicket?: boolean;
    remainingQuantity: TicketCommonProps['quantityRemaining'];
    onDonationChange: (payload: {
        amount: number;
        id: string;
        variantId?: string;
    }) => void;
    urgencySignals: UrgencySignals | null;
    showUnlockedPill: boolean;
    showDiscountedPill: boolean;
}

const TicketDisplayCardContentFullSize: FunctionComponent<
    TicketDisplayCardContentFullSizeProps
> = ({
    category,
    cost,
    currencyFormat,
    currencySymbol,
    description,
    donationAmount,
    feesAndTaxes,
    formattedSalesEndDate,
    formattedSalesStartDate,
    hideSaleDates,
    hideSelect,
    id,
    image,
    includeFee,
    includeTax,
    isAppRebrandActive,
    isBOGOTicket,
    isCurrencySuffix,
    isDonation,
    isFirstTicket,
    name,
    onDonationChange,
    onJoinWaitlist,
    onQuantityChange,
    price,
    priceWithoutDiscounts,
    remainingQuantity,
    selectableQuantities,
    selectedQuantity,
    showDiscountedPill,
    showJoinWaitlist,
    showUnlockedPill,
    statusKey,
    ticketId,
    ticketReseller,
    ticketStatus,
    urgencySignals,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const showTicketReseller = shouldShowTicketReseller(
        statusKey,
        ticketReseller,
    );
    const isCardDisabled =
        getIsCardDisabledByStatus(ticketStatus) &&
        !showJoinWaitlist &&
        !showTicketReseller;
    const isCardSelected = selectedQuantity > 0;

    const handleDonationChange = (amount: string) => {
        onDonationChange?.(
            getDonationChangeActionPayload(ticketId, amount, currencyFormat),
        );
    };
    const baseTicketQuantityPickerProps: Omit<
        TicketQuantityPickerProps,
        | 'ticketStatus'
        | 'formattedSalesStartDate'
        | 'className'
        | 'onJoinWaitlist'
        | 'showJoinWaitlist'
    > = {
        currencyFormat,
        currencySymbol,
        donationAmount,
        isDisabled: ticketStatus === TicketStatus.soldOut,
        isCurrencySuffix,
        isDonation,
        name,
        onDonationChange: handleDonationChange,
        onQuantityChange,
        selectableQuantities,
        selectedQuantity,
        ticketId,
    };
    const ticketQuantityPickerProps: TicketQuantityPickerProps =
        ticketStatus === TicketStatus.salesNotStarted ||
        ticketStatus === TicketStatus.salesStartSoon
            ? {
                  ...baseTicketQuantityPickerProps,
                  ticketStatus,
                  // `formattedSalesStartDate` is always defined when when ticket status is salesNotStarted or
                  // salesStartSoon. For some reason the TypeScript compiler in v4.5.5 is not able of properly narrowing
                  // the type of formattedSalesStartDate relying on the `if` above, so we have to manually cast the value
                  // here.
                  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  formattedSalesStartDate: formattedSalesStartDate!,
              }
            : {
                  ...baseTicketQuantityPickerProps,
                  ticketStatus,
              };

    const shouldShowWaitListButton =
        showJoinWaitlist &&
        (hideSelect ||
            NOT_AVAILABLE_FOR_SALE_STATUSES.includes(
                statusKey as TicketStatus,
            ));

    const ticketCounterOrWaitlistOrReseller = shouldShowWaitListButton ? (
        <TicketJoinWaitlistButton
            onJoinWaitlist={onJoinWaitlist}
            id={id}
            isAppRebrandActive={isAppRebrandActive}
        />
    ) : showTicketReseller ? (
        <TicketResellerButton
            url={ticketReseller?.url}
            text={ticketReseller?.text}
        />
    ) : (
        <TicketQuantityPicker
            {...ticketQuantityPickerProps}
            isAppRebrandActive={isAppRebrandActive}
        />
    );

    const ticketNameStyle = classNames(styles.ticketName, {
        [styles.ticketNameWithReseller]: showTicketReseller,
    });

    return (
        <ChoiceCard
            data-testid="ticket-display-card-content-full-size"
            className={classNames(styles.card, 'eds-l-pad-bot-4', {
                'eds-l-mar-top-4': !(isFirstTicket && isAppRebrandActive),
                'eds-l-pad-top-4': !(isDonation && isAppRebrandActive),
                'eds-l-pad-top-2': isDonation && isAppRebrandActive,
                [styles.cardSelected]: isCardSelected,
                [styles.cardDisabled]: isCardDisabled,
            })}
            disabled={isCardDisabled}
            selected={isCardSelected}
        >
            <Horizontal
                className={classNames('eds-l-pad-hor-4 eds-l-pad-bot-4', {
                    [styles.ticketNameWrapper]: isAppRebrandActive,
                })}
            >
                <TicketName
                    className={ticketNameStyle}
                    disabled={isCardDisabled}
                    isAppRebrandActive={isAppRebrandActive}
                >
                    {name}
                </TicketName>
                {ticketCounterOrWaitlistOrReseller}
            </Horizontal>
            <Divider
                className={classNames(styles.divider, {
                    [styles.dividerCardSelected]: isCardSelected,
                })}
                direction={DividerDirection.horizontal}
            />
            <Vertical
                className={styles.ticketPriceCountDateAndThumbnailWrapper}
            >
                <TicketPriceCountDateAndThumbnail
                    category={category}
                    cost={cost}
                    currencyFormat={currencyFormat}
                    feesAndTaxes={feesAndTaxes}
                    formattedSalesEndDate={formattedSalesEndDate}
                    hideSelect={hideSelect}
                    hideSaleDates={hideSaleDates}
                    image={image}
                    includeFee={includeFee}
                    includeTax={includeTax}
                    isAppRebrandActive={isAppRebrandActive}
                    isBOGOTicket={isBOGOTicket}
                    isDonation={isDonation}
                    ticketStatus={ticketStatus}
                    price={price}
                    priceWithoutDiscounts={priceWithoutDiscounts}
                    showDiscountedPill={showDiscountedPill}
                    showUnlockedPill={showUnlockedPill}
                    remainingQuantity={remainingQuantity}
                    urgencySignals={urgencySignals}
                    showJoinWaitlist={showJoinWaitlist}
                />
            </Vertical>
            <Vertical className="eds-l-pad-hor-4">
                <TicketDescription
                    className="eds-l-pad-top-4"
                    collapsedDisplayAmount={DisplayAmount.small}
                    isAppRebrandActive={isAppRebrandActive}
                >
                    {description}
                </TicketDescription>
            </Vertical>
        </ChoiceCard>
    );
};

export default TicketDisplayCardContentFullSize;
